import React, { useState, memo, useEffect, useLayoutEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Aircraft, Transaction, Event } from '../types/intellijetnet'
import { AircraftTransaction } from '../types/database'
import { formatPrice, formatDate } from '../utils/formatters'
import { getEffectiveCustomColumnValue } from '../utils/overrides'
import QuickNotePopover from './notes/QuickNotePopover'
import { useMarketTableData } from '../hooks/useMarketTableData'
import { useMarketTableSorting } from '../hooks/useMarketTableSorting'
import { useMarketTableEditing } from '../hooks/useMarketTableEditing'

// Import extracted types, constants, and utilities
import type { MarketTableProps, CombinedAircraftData, RowContentData } from './market-table/types'
import { COLUMN_MIN_WIDTHS, calculateContentBasedColumnWidths, formatDamageHistory } from './market-table/utils'
import { MarketTableColGroup, MarketTableHeaderRow } from './market-table/MarketTableHeader'
import { MarketTableRow } from './market-table/MarketTableRow'
export type { ColumnVisibility } from './market-table/types'

export const MarketTable = memo(function MarketTable(props: MarketTableProps) {
  // expanded is now controlled by parent via initialExpanded prop
  const expanded = props.initialExpanded ?? true
  // --- Data hook: all data fetching, overrides, options, relative values, etc. ---
  const {
    overrides,
    customColumns,
    changeInfo,
    companyMap,
    transactionOverrides,
    marketEmps,
    noteCounts,
    options,
    transactionOptions,
    relativeValues,
    relativeValueBreakdowns,
    missingABB,
    isDataLoading,
    hasInitialData,
    combinedAircraft,
    getEffectiveAircraft,
    getEffectiveTransactionAircraft,
    getEffectiveTransactionData,
    setOverrides,
    setTransactionOverrides,
    setCompanyMap,
    setNoteCounts,
    triggerOptionsRefresh,
  } = useMarketTableData(props, expanded)

  // Sorting hook
  const { sortedCombinedAircraft, handleSort } = useMarketTableSorting({
    tableType: props.type,
    combinedAircraft,
    getEffectiveAircraft,
    getEffectiveTransactionAircraft,
    getEffectiveTransactionData,
    relativeValues,
    overrides,
    transactionOverrides,
  })

  // Editing hook
  const {
    editingCell,
    editingOptionsAircraftId,
    setEditingOptionsAircraftId,
    handleTakePriceSave,
    handleAskPriceSave,
    handleFieldSave,
    handleCompanySave,
    handleTransactionPriceSave,
    handleTransactionTakePriceSave,
    handleTransactionAskPriceSave,
    handleTransactionFieldSave,
    handleTransactionCompanySave,
    handleStartEdit,
    handleStartTransactionEdit,
    handleCancelEdit,
    handleOptionsSave,
    handleCancelOptionsEdit,
    handleTransactionOptionsSave,
  } = useMarketTableEditing({
    setOverrides,
    setTransactionOverrides,
    setCompanyMap,
    companyMap,
    triggerOptionsRefresh,
  })

  // Quick note UI state
  const [quickNoteAircraftId, setQuickNoteAircraftId] = useState<number | null>(null)
  const [quickNoteAnchorEl, setQuickNoteAnchorEl] = useState<HTMLElement | null>(null)

  // Extract column visibility settings with defaults
  const cv = props.columnVisibility || {}
  const hideSerialNumber = cv.hideSerialNumber ?? false
  const hideYearMfr = cv.hideYearMfr ?? false
  const hideAftt = cv.hideAftt ?? false
  const hideOwner = cv.hideOwner ?? false
  const hideBroker = cv.hideBroker ?? false
  const hideEmp = cv.hideEmp ?? false
  const hideDmg = cv.hideDmg ?? false
  const hidePax = cv.hidePax ?? false
  const hidePaintInterior = cv.hidePaintInterior ?? false
  const hideOptions = cv.hideOptions ?? false
  const hideDateListed = cv.hideDateListed ?? false
  const hideJnAsk = cv.hideJnAsk ?? false
  const hideAskingPrice = cv.hideAskingPrice ?? false
  const hideExternalColumns = cv.hideExternalColumns ?? false
  const hideRelativeValue = cv.hideRelativeValue ?? false

  // Cell border styling - applied when showCellBorders is true
  const showCellBorders = props.showCellBorders ?? true
  const cellBorderClass = showCellBorders ? 'border border-gray-800' : ''
  const headerBorderClass = showCellBorders ? 'border border-gray-800' : ''

  const navigate = useNavigate()
  const tableScrollRef = useRef<HTMLDivElement>(null)
  const headerScrollRef = useRef<HTMLDivElement>(null)
  const tbodyRef = useRef<HTMLTableSectionElement>(null)
  const [rowHeights, setRowHeights] = useState<number[]>([])
  const rowHeightsRef = useRef<number[]>([])
  const [headerHeight, setHeaderHeight] = useState(0)
  const isScrollingSyncRef = useRef(false)

  // Update parent when expanded state changes
  useEffect(() => {
    props.onExpandedChange?.(expanded)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded])

  // Sync horizontal scroll between header and body
  // Note: Vertical scroll sync to the notes sidebar was removed because
  // both the sidebar and table body are in the same document flow and scroll
  // together naturally via page-level scrolling. The table body div has no
  // fixed height, so it doesn't scroll vertically itself. Setting scrollTop
  // on the sidebar was causing buttons to drift out of alignment with rows.
  const handleTableScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!isScrollingSyncRef.current) {
      isScrollingSyncRef.current = true
      // Sync horizontal scroll to header
      if (headerScrollRef.current) {
        headerScrollRef.current.scrollLeft = e.currentTarget.scrollLeft
      }
      requestAnimationFrame(() => {
        isScrollingSyncRef.current = false
      })
    }
  }

  // Sync horizontal scroll from header to body
  const handleHeaderScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!isScrollingSyncRef.current && tableScrollRef.current) {
      isScrollingSyncRef.current = true
      tableScrollRef.current.scrollLeft = e.currentTarget.scrollLeft
      requestAnimationFrame(() => {
        isScrollingSyncRef.current = false
      })
    }
  }



  // Measure row heights from tbody for the quick notes sidebar
  // Dependencies include loading states because the tbody only exists in the DOM
  // when the table is not in a loading state and has data to render.
  useEffect(() => {
    const tbody = tbodyRef.current
    if (!tbody) {
      return
    }

    const measureRows = () => {
      const rows = tbody.querySelectorAll('tr')
      const heights = Array.from(rows).map(row => row.getBoundingClientRect().height)
      // Only update state if heights actually changed to avoid infinite re-renders
      const prev = rowHeightsRef.current
      if (heights.length !== prev.length || heights.some((h, i) => h !== prev[i])) {
        rowHeightsRef.current = heights
        setRowHeights(heights)
      }
    }

    // Delay initial measurement slightly to ensure DOM has painted
    // (tbody may be in the DOM but rows may not have their final heights yet)
    requestAnimationFrame(() => {
      measureRows()
    })

    // Re-measure on resize
    const observer = new ResizeObserver(measureRows)
    observer.observe(tbody)

    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, sortedCombinedAircraft.length, props.loading, isDataLoading, hasInitialData])

  // Measure sticky header height for the quick notes spacer.
  // useLayoutEffect runs synchronously before paint so the spacer has the
  // correct height on the very first frame after the header appears in the DOM.
  // Dependencies mirror the row-heights effect so this re-runs when data loads.
  useLayoutEffect(() => {
    const header = headerScrollRef.current
    if (!header) return

    const measure = () => {
      const h = header.getBoundingClientRect().height
      setHeaderHeight(prev => (prev !== h ? h : prev))
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(header)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, sortedCombinedAircraft.length, props.loading, isDataLoading, hasInitialData])

  // Calculate content-based column widths (matching PDF preview modal logic)
  const columnWidths = useMemo(() => {
    const visibleColumns: string[] = []

    // Build list of visible columns in display order
    if (!hideSerialNumber) visibleColumns.push('serial')
    if (!hideOwner) visibleColumns.push('owner')
    if (!hideBroker) visibleColumns.push('broker')
    if (!hideYearMfr) visibleColumns.push('year')
    if (!hideAftt) visibleColumns.push('aftt')
    if (!hideEmp) visibleColumns.push('emp')
    if (!hideDmg) visibleColumns.push('damage')
    if (!hidePax) visibleColumns.push('pax')
    if (!hidePaintInterior) visibleColumns.push('paintInterior')

    // Custom columns
    const customColumnCount = customColumns.length
    for (let i = 0; i < customColumnCount; i++) {
      visibleColumns.push(`custom_${i}`)
    }

    if (!hideOptions) visibleColumns.push('options')

    // Type-specific columns
    if (props.type === 'for-sale') {
      if (!hideDateListed) visibleColumns.push('dateListed')
      if (!hideJnAsk) visibleColumns.push('jnAsk')
      if (!hideAskingPrice) visibleColumns.push('askingTake')
      if (!hideExternalColumns) visibleColumns.push('externalPrices')
    } else if (props.type === 'sold') {
      visibleColumns.push('datesSold')
      if (!hideAskingPrice) visibleColumns.push('pricesSold')
      if (!hideExternalColumns) visibleColumns.push('externalPrices')
    } else if (props.type === 'removed') {
      visibleColumns.push('dateRemoved')
      if (!hideExternalColumns) visibleColumns.push('externalPrices')
    }

    if (!hideRelativeValue) visibleColumns.push('relativeValue')

    // Build row content data for measurement
    const rowData: RowContentData[] = sortedCombinedAircraft.map(item => {
      const effectiveAircraft = props.type === 'sold'
        ? getEffectiveTransactionAircraft(item.aircraft, item.aircraftTransaction)
        : getEffectiveAircraft(item.aircraft)

      const override = overrides.get(item.aircraft.aircraft_id)

      return {
        serial: `${item.aircraft.sernbr || '-'}\n${item.aircraft.regnbr || '-'}`,
        year: `${effectiveAircraft.yearmfr || '-'}\n${effectiveAircraft.yeardlv || '-'}`,
        aftt: `${effectiveAircraft.aftt?.toLocaleString() || '-'}`,
        emp: effectiveAircraft.emp || '-',
        damage: formatDamageHistory(effectiveAircraft.damagehistoryyn),
        pax: effectiveAircraft.pax?.toString() || '-',
        paintInterior: `${effectiveAircraft.paint_date ?? '-'}\n${effectiveAircraft.interior_date ?? '-'}`,
        dateListed: item.aircraft.datelisted ? formatDate(item.aircraft.datelisted) : '-',
        jnAsk: formatPrice(effectiveAircraft.asking),
        askingTake: formatPrice(effectiveAircraft.asking),
        pricesSold: '-',
        externalPrices: override?.external_asking_price ? formatPrice(override.external_asking_price.toString()) : '-',
        datesSold: '-',
        dateRemoved: item.event?.date ? formatDate(item.event.date) : '-',
        // Custom columns
        custom_0: getEffectiveCustomColumnValue(1, item.aircraft, override) || '-',
        custom_1: getEffectiveCustomColumnValue(2, item.aircraft, override) || '-',
        custom_2: getEffectiveCustomColumnValue(3, item.aircraft, override) || '-'
      }
    })

    // Build custom column titles map
    const customColumnTitles = new Map<number, string>()
    customColumns.forEach((col, i) => {
      customColumnTitles.set(i, col.column_title)
    })

    return calculateContentBasedColumnWidths(visibleColumns, rowData, customColumnTitles)
  }, [
    hideSerialNumber, hideOwner, hideBroker, hideYearMfr, hideAftt, hideEmp,
    hideDmg, hidePax, hidePaintInterior, hideOptions, hideDateListed, hideJnAsk, hideAskingPrice,
    hideExternalColumns, hideRelativeValue, props.type, customColumns,
    sortedCombinedAircraft, getEffectiveAircraft, getEffectiveTransactionAircraft, overrides
  ])

  const handleRowClick = (item: Aircraft | { aircraft: Aircraft; transaction?: Transaction | null; event?: Event | null; aircraftTransaction?: AircraftTransaction | null }, isOffMarket = false, isTemporary = false) => {
    // Get the aircraft ID and unique row ID for scroll restoration
    let aircraftId: number
    let rowId: string

    if (props.type === 'for-sale') {
      aircraftId = (item as Aircraft).aircraft_id
      rowId = `${aircraftId}`
    } else if (props.type === 'sold') {
      const soldItem = item as { aircraft: Aircraft; transaction?: Transaction | null; aircraftTransaction?: AircraftTransaction | null }
      aircraftId = soldItem.aircraft.aircraft_id
      // Use aircraftTransaction ID if available, otherwise fall back to transaction ID or just aircraft ID
      const transId = soldItem.aircraftTransaction?.transaction_id || soldItem.transaction?.trans_id || 'static'
      rowId = `sold-${aircraftId}-${transId}`
    } else {
      const removedItem = item as { aircraft: Aircraft; event?: Event | null }
      aircraftId = removedItem.aircraft.aircraft_id
      const eventId = removedItem.event?.event_id || 'static'
      rowId = `removed-${aircraftId}-${eventId}`
    }

    // Save state before navigating, including the aircraft ID and row ID for scroll restoration
    if (props.onNavigateToDetail) {
      props.onNavigateToDetail(aircraftId, rowId)
    }

    if (props.type === 'for-sale') {
      if (isOffMarket) {
        navigate(`/market/${props.modelId}/off-market/${aircraftId}`)
      } else {
        navigate(`/market/${props.modelId}/for-sale/${aircraftId}`)
      }
    } else if (props.type === 'sold') {
      const { transaction, aircraft, aircraftTransaction } = item as { aircraft: Aircraft; transaction: Transaction | null; aircraftTransaction?: AircraftTransaction | null }
      if (isOffMarket) {
        navigate(`/market/${props.modelId}/off-market/${aircraft.aircraft_id}`)
      } else if (isTemporary) {
        // For temporary aircraft, navigate to aircraft details page using for-sale route
        // This will show the aircraft details with movement controls
        navigate(`/market/${props.modelId}/for-sale/${aircraft.aircraft_id}`)
      } else if (aircraftTransaction?.transaction_id) {
        // Navigate to aircraft transaction snapshot page
        navigate(`/market/${props.modelId}/sold-snapshot/${aircraftTransaction.transaction_id}`)
      } else if (transaction?.trans_id && transaction.trans_id > 0) {
        // Only navigate to transaction page for real API transactions (positive IDs)
        navigate(`/market/${props.modelId}/sold/${transaction.trans_id}`)
      } else {
        // Fallback to aircraft details page
        navigate(`/market/${props.modelId}/for-sale/${aircraft.aircraft_id}`)
      }
    } else {
      const { event, aircraft } = item as { aircraft: Aircraft; event: Event | null }
      if (isOffMarket) {
        navigate(`/market/${props.modelId}/off-market/${aircraft.aircraft_id}`)
      } else if (isTemporary) {
        // For temporary aircraft, navigate to aircraft details page using for-sale route
        // This will show the aircraft details with movement controls
        navigate(`/market/${props.modelId}/for-sale/${aircraft.aircraft_id}`)
      } else if (event?.event_id && !event.event_id.startsWith('temp-')) {
        // Only navigate to event page for real events (not temporary mock events)
        navigate(`/market/${props.modelId}/removed/${event.event_id}`)
      } else {
        // Fallback: if no valid event, navigate to for-sale page as a fallback
        navigate(`/market/${props.modelId}/for-sale/${aircraft.aircraft_id}`)
      }
    }
  }



  const renderLoadingSkeleton = () => {
    return (
      <div className={`relative h-[90vh] overflow-hidden border border-gray-200 bg-white shadow rounded-lg`}>
        <div className="animate-pulse">
          {/* Table header skeleton */}
          <div className={`${props.colorScheme.subHeader} px-6 py-3 flex space-x-6`}>
            <div className="h-4 bg-gray-300 rounded w-32 opacity-50"></div>
            <div className="h-4 bg-gray-300 rounded w-24 opacity-50"></div>
            <div className="h-4 bg-gray-300 rounded w-20 opacity-50"></div>
            <div className="h-4 bg-gray-300 rounded w-16 opacity-50"></div>
            <div className="h-4 bg-gray-300 rounded w-16 opacity-50"></div>
            <div className="h-4 bg-gray-300 rounded w-24 opacity-50"></div>
            <div className="h-4 bg-gray-300 rounded w-28 opacity-50"></div>
            <div className="h-4 bg-gray-300 rounded w-24 opacity-50"></div>
          </div>
          {/* Table rows skeleton */}
          <div className="bg-white divide-y divide-gray-200">
            {[...Array(8)].map((_, idx) => (
              <div key={idx} className="px-6 py-4 flex space-x-6">
                <div className="space-y-2 w-32">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-3 bg-gray-100 rounded w-3/4"></div>
                  <div className="h-2 bg-gray-50 rounded w-1/2"></div>
                </div>
                <div className="space-y-2 w-24">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-20 mt-2"></div>
                <div className="h-4 bg-gray-200 rounded w-16 mt-2"></div>
                <div className="h-4 bg-gray-200 rounded w-16 mt-2"></div>
                <div className="h-4 bg-gray-200 rounded w-24 mt-2"></div>
                <div className="h-4 bg-gray-200 rounded w-28 mt-2"></div>
                <div className="h-4 bg-gray-200 rounded w-24 mt-2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const renderTableContent = () => {
    // Show loading skeleton only if we're actually loading and don't have data yet
    if (props.loading || (isDataLoading && !hasInitialData)) {
      return renderLoadingSkeleton()
    }

    const hasData = props.type === 'for-sale' ? props.aircraft.length > 0 : props.data.length > 0

    if (!hasData) {
      const emptyMessage = props.type === 'for-sale'
        ? 'No aircraft currently for sale'
        : props.type === 'sold'
        ? 'No aircraft sales found'
        : 'No aircraft removed from market in the past 6 months'

      return (
        <div className="px-6 py-12 text-center text-gray-500 bg-white shadow rounded-lg">
          {emptyMessage}
        </div>
      )
    }

    // Build colgroup for content-based column widths (using percentages with minimum widths)
    // Collect visible columns for minimum width calculation
    const visibleColumnKeys: string[] = []
    if (!hideSerialNumber) visibleColumnKeys.push('serial')
    if (!hideOwner) visibleColumnKeys.push('owner')
    if (!hideBroker) visibleColumnKeys.push('broker')
    if (!hideYearMfr) visibleColumnKeys.push('year')
    if (!hideAftt) visibleColumnKeys.push('aftt')
    if (!hideEmp) visibleColumnKeys.push('emp')
    if (!hideDmg) visibleColumnKeys.push('damage')
    if (!hidePax) visibleColumnKeys.push('pax')
    if (!hidePaintInterior) visibleColumnKeys.push('paintInterior')
    customColumns.forEach((_, i) => visibleColumnKeys.push(`custom_${i}`))
    if (!hideOptions) visibleColumnKeys.push('options')
    if (props.type === 'for-sale') {
      if (!hideDateListed) visibleColumnKeys.push('dateListed')
      if (!hideJnAsk) visibleColumnKeys.push('jnAsk')
      if (!hideAskingPrice) visibleColumnKeys.push('askingTake')
      if (!hideExternalColumns) visibleColumnKeys.push('externalPrices')
    } else if (props.type === 'sold') {
      visibleColumnKeys.push('datesSold')
      if (!hideAskingPrice) visibleColumnKeys.push('pricesSold')
      if (!hideExternalColumns) visibleColumnKeys.push('externalPrices')
    } else if (props.type === 'removed') {
      visibleColumnKeys.push('dateRemoved')
      if (!hideExternalColumns) visibleColumnKeys.push('externalPrices')
    }
    if (!hideRelativeValue) visibleColumnKeys.push('relativeValue')

    // Calculate dynamic minimum table width based on visible columns
    const calculatedMinTableWidth = visibleColumnKeys.reduce((sum, key) => {
      return sum + (COLUMN_MIN_WIDTHS[key] || 60)
    }, 0)
    // Add some padding for cell borders and spacing
    const minTableWidth = `${calculatedMinTableWidth + 20}px`



    return (
      <div className="flex">
        {/* Quick Notes Sidebar - outside the table */}
        <div className="flex-shrink-0 flex flex-col" style={{ width: '28px' }}>
          {/* Spacer to match sticky header height — covers buttons behind it while scrolling */}
          <div
            className={`sticky ${props.stickyHeaderClass || 'top-0'} z-30 bg-gray-50`}
            style={{ height: headerHeight || 0 }}
          />
          {/* Note buttons aligned to row heights — scrolls naturally with page */}
          <div className="flex-1">

            {sortedCombinedAircraft.map((combinedItem, index) => {
              const aircraftId = combinedItem.aircraft.aircraft_id
              const height = rowHeights[index] || 0
              if (height === 0) return null
              return (
                <div
                  key={`note-${aircraftId}-${index}`}
                  className="flex items-center justify-center relative"
                  style={{ height: `${height}px` }}
                >
                  <button
                    onClick={(e) => {
                      if (quickNoteAircraftId === aircraftId) {
                        setQuickNoteAircraftId(null)
                        setQuickNoteAnchorEl(null)
                      } else {
                        setQuickNoteAircraftId(aircraftId)
                        setQuickNoteAnchorEl(e.currentTarget)
                      }
                    }}
                    className={`flex items-center justify-center transition-colors duration-150 rounded ${
                      noteCounts.get(aircraftId)
                        ? 'text-purple-700 hover:bg-purple-100'
                        : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
                    }`}
                    title={`${noteCounts.get(aircraftId) || 0} note(s) — click to add`}
                  >
                    <div className="relative">
                      <svg className="w-4 h-4" fill={noteCounts.get(aircraftId) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                      </svg>
                      {(noteCounts.get(aircraftId) || 0) > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 bg-purple-700 text-white text-[9px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center leading-none">
                          {noteCounts.get(aircraftId)}
                        </span>
                      )}
                    </div>
                  </button>
                  {quickNoteAircraftId === aircraftId && (
                    <QuickNotePopover
                      aircraftId={aircraftId}
                      serialNumber={combinedItem.aircraft.sernbr}
                      noteCount={noteCounts.get(aircraftId) || 0}
                      anchorEl={quickNoteAnchorEl}
                      onNoteSaved={() => {
                        setNoteCounts(prev => {
                          const newMap = new Map(prev)
                          newMap.set(aircraftId, (prev.get(aircraftId) || 0) + 1)
                          return newMap
                        })
                      }}
                      onClose={() => {
                        setQuickNoteAircraftId(null)
                        setQuickNoteAnchorEl(null)
                      }}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Table container */}
        <div className="relative flex-1 min-w-0 bg-white shadow rounded-lg">
          {/* Sticky Header */}
          <div
            ref={headerScrollRef}
            onScroll={handleHeaderScroll}
            className={`overflow-x-auto overflow-y-hidden sticky ${props.stickyHeaderClass || 'top-0'} z-30 scrollbar-hide shadow-sm border-x border-gray-200`}
          >
            <table style={{ tableLayout: 'fixed', width: '100%', minWidth: minTableWidth }}>
              <MarketTableColGroup tableType={props.type} columnVisibility={cv} columnWidths={columnWidths} customColumns={customColumns} />
              <thead className={`${props.colorScheme.subHeader}`}>
                <MarketTableHeaderRow tableType={props.type} columnVisibility={cv} customColumns={customColumns} colorScheme={props.colorScheme} headerBorderClass={headerBorderClass} handleSort={handleSort} />
              </thead>
            </table>
          </div>

          {/* Scrollable Body */}
          <div
            ref={tableScrollRef}
            onScroll={handleTableScroll}
            className="overflow-x-auto border-x border-b border-gray-200"
          >
          <table className="divide-y divide-gray-200" style={{ tableLayout: 'fixed', width: '100%', minWidth: minTableWidth }}>
            <MarketTableColGroup tableType={props.type} columnVisibility={cv} columnWidths={columnWidths} customColumns={customColumns} />
            <tbody ref={tbodyRef} className="bg-white divide-y divide-gray-200">
            {sortedCombinedAircraft.map((combinedItem: CombinedAircraftData) => (
              <MarketTableRow
                key={`${combinedItem.source}-${combinedItem.aircraft.aircraft_id}-${combinedItem.aircraftTransaction?.transaction_id || combinedItem.transaction?.trans_id || combinedItem.event?.event_id || 'static'}`}
                combinedItem={combinedItem}
                tableType={props.type}
                isEditMode={props.isEditMode}
                cellBorderClass={cellBorderClass}
                columnVisibility={cv}
                overrides={overrides}
                transactionOverrides={transactionOverrides}
                companyMap={companyMap}
                changeInfo={changeInfo}
                customColumns={customColumns}
                marketEmps={marketEmps}
                options={options}
                transactionOptions={transactionOptions}
                relativeValues={relativeValues}
                relativeValueBreakdowns={relativeValueBreakdowns}
                missingABB={missingABB}
                editingCell={editingCell}
                editingOptionsAircraftId={editingOptionsAircraftId}
                setEditingOptionsAircraftId={setEditingOptionsAircraftId}
                getEffectiveAircraft={getEffectiveAircraft}
                getEffectiveTransactionAircraft={getEffectiveTransactionAircraft}
                getEffectiveTransactionData={getEffectiveTransactionData}
                handleRowClick={handleRowClick}
                handleTakePriceSave={handleTakePriceSave}
                handleAskPriceSave={handleAskPriceSave}
                handleFieldSave={handleFieldSave}
                handleCompanySave={handleCompanySave}
                handleTransactionPriceSave={handleTransactionPriceSave}
                handleTransactionTakePriceSave={handleTransactionTakePriceSave}
                handleTransactionAskPriceSave={handleTransactionAskPriceSave}
                handleTransactionFieldSave={handleTransactionFieldSave}
                handleTransactionCompanySave={handleTransactionCompanySave}
                handleStartEdit={handleStartEdit}
                handleStartTransactionEdit={handleStartTransactionEdit}
                handleCancelEdit={handleCancelEdit}
                handleOptionsSave={handleOptionsSave}
                handleCancelOptionsEdit={handleCancelOptionsEdit}
                handleTransactionOptionsSave={handleTransactionOptionsSave}
              />
            ))}
            </tbody>
          </table>
        </div>
        </div>
      </div>
    )
  }

  // When not expanded, don't render anything (for backward compatibility with collapsed state)
  if (!expanded) {
    return null
  }

  return (
    <div>
      {renderTableContent()}
    </div>
  )
})